import { AsyncPipe, NgClass } from '@angular/common';
import {
    Component,
    Input,
    OnDestroy,
    OnInit,
    Signal,
    signal,
    WritableSignal,
} from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatSliderModule } from '@angular/material/slider';
import { Store } from '@ngrx/store';
import { ConfigService } from '@polaris-customerweb/sharedcomponents.config';
import { RideType } from '@polaris-customerweb/sharedcomponents.core';
import {
    ImageCdnSrcDirective,
    RideTypeService,
} from '@polaris-customerweb/sharedcomponents.shared';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { combineLatest, merge, Observable, Subject } from 'rxjs';
import { filter, map, takeWhile, tap } from 'rxjs/operators';
import { AnalyticsDataDirective } from 'src/app/directives/analytics-data.directive';
import { AnalyticsDirective } from 'src/app/directives/analytics.directive';
import { AppState } from 'src/app/models/app-state';
import { MapDisplay } from 'src/app/models/map-display';
import { RideConditionService } from 'src/app/services/ride-condition.service';
import {
    SetIsShowingAdventures,
    SetIsShowingDealers,
    SetIsShowingFood,
    SetIsShowingFuel,
    SetIsShowingGeofences,
    SetIsShowingInProgressRideTrack,
    SetIsShowingLodging,
    SetIsShowingPlannedRides,
    SetIsShowingTrackedRides,
    SetIsShowingTrails,
    SetIsShowingUnknown,
    SetIsShowingWaypoints,
    SetMaxRideConditionAgeInHours,
    SetShouldShowCloudCover,
    SetShouldShowCondition,
    SetShouldShowHeatmap,
    SetShouldShowLand,
    SetShouldShowRadar,
    SetShouldShowRideCondition,
    SetShouldShowSatellite,
    SetShouldShowSnowDepth,
    SetShouldShowTrailDifficulty,
    ShowTrailTypeChooser,
} from 'src/app/state/map/layer-visibility/layer-visibility.actions';
import {
    selectIsShowingAdventures,
    selectIsShowingCloudCover,
    selectIsShowingConditions,
    selectIsShowingDealers,
    selectIsShowingFood,
    selectIsShowingFuel,
    selectIsShowingGeofences,
    selectIsShowingHeatmap,
    selectIsShowingInProgressRide,
    selectIsShowingLand,
    selectIsShowingLodging,
    selectIsShowingPlannedRides,
    selectIsShowingRadar,
    selectIsShowingRideConditions,
    selectIsShowingSatellite,
    selectIsShowingSnowDepth,
    selectIsShowingTrackedRides,
    selectIsShowingTrailDifficulty,
    selectIsShowingTrails,
    selectIsShowingUnknown,
    selectIsShowingWaypoints,
    selectMaxRideConditionsAgeInHours,
    selectShowTrailTypeChooser,
} from 'src/app/state/map/layer-visibility/layer-visibility.reducer';
import {} from 'src/app/state/map/map.actions';
import { SnowOrvToggleComponent } from '../../snow-orv-toggle/snow-orv-toggle.component';
import {
    MapToolsLayerGroup,
    MapToolsSelectedGroupSummaryComponent,
} from './map-tools-selected-group-summary/map-tools-selected-group-summary.component';
import { OffroadTrailListComponent } from './offroad-trail-list/offroad-trail-list.component';
import { SnowTrailListComponent } from './snow-trail-list/snow-trail-list.component';

@Component({
    imports: [
        AnalyticsDataDirective,
        AnalyticsDirective,
        AngularSvgIconModule,
        AsyncPipe,
        FormsModule,
        ImageCdnSrcDirective,
        MapToolsSelectedGroupSummaryComponent,
        MatSliderModule,
        MatSlideToggleModule,
        NgClass,
        OffroadTrailListComponent,
        SnowOrvToggleComponent,
        SnowTrailListComponent,
    ],
    selector: 'rdx-map-drawer',
    styleUrls: ['./map-drawer.component.scss'],
    templateUrl: './map-drawer.component.html',
})
export class MapDrawerComponent implements OnDestroy, OnInit {
    @Input() hasViewCurrentRidePermission = false;

    brand: string;
    isMapLayersDrawerOpen = false;
    isMapTypeDrawerOpen = false;
    enableMapTypeToggle: boolean | undefined;
    areGeofencesEnabled: boolean | undefined;
    isLandInfoEnabled: boolean;
    isTrailDifficultyEnabled: boolean;
    isShowingDealers = true;
    isShowingAdventures = true;
    isShowingPlannedRides = true;
    isShowingPois = true;
    isShowingTrackedRides = true;
    isShowingInProgressRide = true;
    isShowingWaypoints = true;
    isShowingFoodPlaces = false;
    isShowingFuelPlaces = false;
    isShowingLodgingPlaces = false;
    isShowingUnknownPlaces = false;
    isShowingGeofences = false;
    isShowingSatellite = false;
    isShowingRadarWeather = false;
    isShowingLandInfo = false;
    isShowingCloudCover = false;
    isShowingSnowDepth = false;
    isShowingRideCondition = false;
    isShowingCondition = false;
    isShowingTrailDifficulty = false;
    isShowingHeatmap = false;
    isMapTypeSno = false;
    isHeatMapEnabled = false;
    isHazardReportingEnabled = false;
    isSnoRideType$: Observable<boolean>;
    rideType$: Observable<RideType | undefined>;
    maxRideConditionAgeInHours$: Observable<number>;
    rideConditionSliderLabel$: Observable<string>;
    maxRideConditionAgeLabelSubject = new Subject<number>();
    $maxSnowRideConditionAgeInHours: Signal<number>;
    $maxOffRideConditionAgeInHours: Signal<number>;
    $snowRideConditionSliderLabel: WritableSignal<string>;
    $offRideConditionSliderLabel: WritableSignal<string>;
    isShowingAtvTrails = true;
    isShowingDesignatedUnpavedTrails = true;
    isShowingDesignatedPavedTrails = true;
    isShowingTrails = true;
    showTrailTypeChooser: boolean;
    activeLayers: { key: MapToolsLayerGroup; value: boolean }[] = [];

    private isDestroyed = false;

    constructor(
        private configService: ConfigService,
        private store: Store<AppState>,
        rideTypeService: RideTypeService,
        private rideConditionService: RideConditionService
    ) {
        this.brand = configService.brand;
        this.isHeatMapEnabled = configService.isOffRoad;
        this.isHazardReportingEnabled =
            configService.featureFlags.isHazardReportingEnabled;

        this.$maxSnowRideConditionAgeInHours =
            this.rideConditionService.$snowRideConditionsDuration;
        this.$maxOffRideConditionAgeInHours =
            this.rideConditionService.$offRideConditionsDuration;
        this.$snowRideConditionSliderLabel = signal(
            this.formatDuration(this.$maxSnowRideConditionAgeInHours())
        );

        this.$offRideConditionSliderLabel = signal(
            this.formatDuration(this.$maxOffRideConditionAgeInHours(), 0)
        );

        this.isSnoRideType$ = toObservable(rideTypeService.$isSnoRideType);
        this.rideType$ = toObservable(rideTypeService.$rideType);
    }

    ngOnInit(): void {
        this.store
            .select(selectShowTrailTypeChooser)
            .pipe(
                filter((showTrailType) => {
                    return showTrailType;
                })
            )
            .subscribe(() => {
                this.isMapLayersDrawerOpen = true;
                this.isMapTypeDrawerOpen = true;
                this.store.dispatch(new ShowTrailTypeChooser(false));
            });

        if (this.enableMapTypeToggle === undefined) {
            this.enableMapTypeToggle =
                this.configService.featureFlags.isRideTypeToggleEnabled;
        }

        if (this.areGeofencesEnabled === undefined) {
            this.areGeofencesEnabled =
                this.configService.featureFlags.areGeofencesEnabled;
        }

        this.isLandInfoEnabled =
            this.configService.isOffRoad &&
            this.configService.featureFlags.isLandInfoEnabled;

        this.isTrailDifficultyEnabled = this.configService.isOffRoad;

        this.addLayerVisibilityListeners();

        this.maxRideConditionAgeInHours$ = this.store.select(
            selectMaxRideConditionsAgeInHours
        );
        this.rideConditionSliderLabel$ = merge(
            this.maxRideConditionAgeInHours$,
            this.maxRideConditionAgeLabelSubject.asObservable()
        ).pipe(
            map((ageInHours) => {
                if (ageInHours < 48) {
                    return `${ageInHours} hours`;
                } else {
                    const days = Math.floor(ageInHours / 24);
                    return `${days} days`;
                }
            })
        );
    }

    ngOnDestroy(): void {
        this.isDestroyed = true;
    }

    toggleMapLayersDrawerVisibility() {
        this.isMapLayersDrawerOpen = !this.isMapLayersDrawerOpen;
    }

    toggleTrailTypeDrawer() {
        this.isMapTypeDrawerOpen = !this.isMapTypeDrawerOpen;
    }

    setIsShowingDealers(isShowing: boolean) {
        this.store.dispatch(new SetIsShowingDealers(isShowing));
    }

    setIsShowingAdventures(isShowing: boolean) {
        this.store.dispatch(new SetIsShowingAdventures(isShowing));
    }

    setIsShowingPlannedRides(isShowing: boolean) {
        this.store.dispatch(new SetIsShowingPlannedRides(isShowing));
    }

    setIsShowingFood(isShowing: boolean) {
        this.store.dispatch(new SetIsShowingFood(isShowing));
    }

    setIsShowingFuel(isShowing: boolean) {
        this.store.dispatch(new SetIsShowingFuel(isShowing));
    }

    setIsShowingLodging(isShowing: boolean) {
        this.store.dispatch(new SetIsShowingLodging(isShowing));
    }

    setIsShowingUnknown(isShowing: boolean) {
        this.store.dispatch(new SetIsShowingUnknown(isShowing));
    }

    setIsShowingGeofences(isShowing: boolean) {
        this.store.dispatch(new SetIsShowingGeofences(isShowing));
    }

    setIsShowingTrackedRides(isShowing: boolean) {
        this.store.dispatch(new SetIsShowingTrackedRides(isShowing));
    }
    setIsShowingInProgressRideTrack(isShowing: boolean) {
        this.store.dispatch(new SetIsShowingInProgressRideTrack(isShowing));
    }

    setIsShowingWaypoints(isShowing: boolean) {
        this.store.dispatch(new SetIsShowingWaypoints(isShowing));
    }

    setIsShowingSatellite(newValue: boolean): void {
        this.store.dispatch(new SetShouldShowSatellite(newValue));
    }

    setIsShowingRadarWeather(newValue: boolean): void {
        this.store.dispatch(new SetShouldShowRadar(newValue));
    }

    setIsShowingLandInfo(newValue: boolean): void {
        this.store.dispatch(new SetShouldShowLand(newValue));
    }

    setIsShowingCloudCover(newValue: boolean): void {
        this.store.dispatch(new SetShouldShowCloudCover(newValue));
    }

    setIsShowingSnowDepth(newValue: boolean): void {
        this.store.dispatch(new SetShouldShowSnowDepth(newValue));
    }

    setConditions(newValue: boolean): void {
        this.store.dispatch(new SetShouldShowCondition(newValue));
    }

    setRideConditions(newValue: boolean): void {
        this.store.dispatch(new SetShouldShowRideCondition(newValue));
    }

    setRideConditionsDuration(duration: number): void {
        this.store.dispatch(new SetMaxRideConditionAgeInHours(duration));
    }

    setIsShowingTrails(isShowing: boolean) {
        this.store.dispatch(new SetIsShowingTrails(isShowing));
    }

    setMapDisplay(mapDisplay: MapDisplay): void {
        const mapExtents$ = mapDisplay.mapExtents$.pipe(
            filter((extents) => Boolean(extents))
        );

        merge(mapDisplay.mapClick$, mapExtents$)
            .pipe(takeWhile(() => !this.isDestroyed))
            .subscribe(() => {
                this.isMapLayersDrawerOpen = false;
            });
    }

    setTrailDifficulty(newValue: boolean): void {
        this.store.dispatch(new SetShouldShowTrailDifficulty(newValue));
    }

    setIsShowingHeatmap(newValue: boolean): void {
        this.store.dispatch(new SetShouldShowHeatmap(newValue));
    }

    setSnowRideConditionsDuration(duration: number): void {
        this.rideConditionService.setSnowRideConditionsDuration(duration);
        this.setSnowRideConditionSliderLabel(duration);
    }

    setOffRideConditionsDuration(duration: number): void {
        this.rideConditionService.setOffRideConditionsDuration(duration);
        this.setOffRideConditionsSliderLabel(duration);
    }

    setSnowRideConditionSliderLabel(val: number) {
        this.$snowRideConditionSliderLabel.set(this.formatDuration(val));
    }

    setOffRideConditionsSliderLabel(val: number) {
        this.$offRideConditionSliderLabel.set(this.formatDuration(val, 0));
    }
    private formatDuration(hours?: number, showHoursIfLessThan = 48): string {
        if (hours === undefined) return '';
        if (hours < showHoursIfLessThan) return $localize`Last ${hours} hours`;

        const days = Math.floor(hours / 24);
        return days === 1
            ? $localize`Last ${days} day`
            : $localize`Last ${days} days`;
    }

    private addLayerVisibilityListeners(): void {
        this.store
            .select(selectIsShowingDealers)
            .pipe(
                this.setVisibleLayer(MapToolsLayerGroup.Dealer),
                takeWhile(() => !this.isDestroyed)
            )
            .subscribe((isShowingDealers) => {
                this.isShowingDealers = isShowingDealers;
            });

        this.store
            .select(selectIsShowingAdventures)
            .pipe(
                this.setVisibleLayer(MapToolsLayerGroup.Adventure),
                takeWhile(() => !this.isDestroyed)
            )
            .subscribe((isShowingAdventures) => {
                this.isShowingAdventures = isShowingAdventures;
            });

        this.store
            .select(selectIsShowingPlannedRides)
            .pipe(
                this.setVisibleLayer(MapToolsLayerGroup.PlannedRides),
                takeWhile(() => !this.isDestroyed)
            )
            .subscribe(
                (isShowingPlannedRides) =>
                    (this.isShowingPlannedRides = isShowingPlannedRides)
            );

        this.store
            .select(selectIsShowingTrackedRides)
            .pipe(
                this.setVisibleLayer(MapToolsLayerGroup.TrackedRides),
                takeWhile(() => !this.isDestroyed)
            )
            .subscribe(
                (isShowingTrackedRides) =>
                    (this.isShowingTrackedRides = isShowingTrackedRides)
            );

        this.store
            .select(selectIsShowingInProgressRide)
            .pipe(
                map(
                    (isShowing) =>
                        isShowing && this.hasViewCurrentRidePermission
                ),
                this.setVisibleLayer(MapToolsLayerGroup.InProgressRide),
                takeWhile(() => !this.isDestroyed)
            )
            .subscribe(
                (isShowingInProgressRide) =>
                    (this.isShowingInProgressRide = isShowingInProgressRide)
            );

        this.store
            .select(selectIsShowingFood)
            .pipe(
                this.setVisibleLayer(MapToolsLayerGroup.Food),
                takeWhile(() => !this.isDestroyed)
            )
            .subscribe(
                (isShowingFoodPlaces) =>
                    (this.isShowingFoodPlaces = isShowingFoodPlaces)
            );

        this.store
            .select(selectIsShowingFuel)
            .pipe(
                this.setVisibleLayer(MapToolsLayerGroup.Fuel),
                takeWhile(() => !this.isDestroyed)
            )
            .subscribe(
                (isShowingFuelPlaces) =>
                    (this.isShowingFuelPlaces = isShowingFuelPlaces)
            );

        this.store
            .select(selectIsShowingLodging)
            .pipe(
                this.setVisibleLayer(MapToolsLayerGroup.Lodging),
                takeWhile(() => !this.isDestroyed)
            )
            .subscribe(
                (isShowingLodgingPlaces) =>
                    (this.isShowingLodgingPlaces = isShowingLodgingPlaces)
            );

        this.store
            .select(selectIsShowingUnknown)
            .pipe(
                this.setVisibleLayer(MapToolsLayerGroup.Unknown),
                takeWhile(() => !this.isDestroyed)
            )
            .subscribe(
                (isShowingUnknownPlaces) =>
                    (this.isShowingUnknownPlaces = isShowingUnknownPlaces)
            );

        if (this.areGeofencesEnabled) {
            this.store
                .select(selectIsShowingGeofences)
                .pipe(
                    this.setVisibleLayer(MapToolsLayerGroup.Areas),
                    takeWhile(() => !this.isDestroyed)
                )
                .subscribe(
                    (isShowingGeofences) =>
                        (this.isShowingGeofences = isShowingGeofences)
                );
        }

        this.store
            .select(selectIsShowingWaypoints)
            .pipe(
                this.setVisibleLayer(MapToolsLayerGroup.Waypoint),
                takeWhile(() => !this.isDestroyed)
            )
            .subscribe(
                (isShowingWaypoints) =>
                    (this.isShowingWaypoints = isShowingWaypoints)
            );

        this.store
            .select(selectIsShowingSatellite)
            .pipe(
                this.setVisibleLayer(MapToolsLayerGroup.Satellite),
                takeWhile(() => !this.isDestroyed)
            )
            .subscribe((isShowingSatellite) => {
                this.isShowingSatellite = isShowingSatellite;
            });

        this.store
            .select(selectIsShowingRadar)
            .pipe(
                this.setVisibleLayer(MapToolsLayerGroup.Radar),
                takeWhile(() => !this.isDestroyed)
            )
            .subscribe(
                (isShowingRadar) =>
                    (this.isShowingRadarWeather = isShowingRadar)
            );

        this.store
            .select(selectIsShowingLand)
            .pipe(
                this.setVisibleLayer(MapToolsLayerGroup.Land),
                takeWhile(() => !this.isDestroyed)
            )
            .subscribe(
                (isShowingLand) => (this.isShowingLandInfo = isShowingLand)
            );

        this.store
            .select(selectIsShowingCloudCover)
            .pipe(
                this.setVisibleLayer(MapToolsLayerGroup.CloudCover),
                takeWhile(() => !this.isDestroyed)
            )
            .subscribe((isShowingCloudCover) => {
                this.isShowingCloudCover = isShowingCloudCover;
            });

        combineLatest([
            this.store.select(selectIsShowingSnowDepth),
            this.isSnoRideType$,
        ])
            .pipe(
                map(
                    ([isShowingSnowDepth, isRideTypeSnow]) =>
                        isShowingSnowDepth && isRideTypeSnow
                ),
                this.setVisibleLayer(MapToolsLayerGroup.SnowDepth),
                takeWhile(() => !this.isDestroyed)
            )
            .subscribe(
                (isShowingSnowDepth) =>
                    (this.isShowingSnowDepth = isShowingSnowDepth)
            );

        this.store
            .select(selectIsShowingConditions)
            .pipe(
                map(
                    (isShowingConditions) =>
                        isShowingConditions && this.isHazardReportingEnabled
                ),
                this.setVisibleLayer(MapToolsLayerGroup.Conditions),
                takeWhile(() => !this.isDestroyed)
            )
            .subscribe(
                (isShowingCondition) =>
                    (this.isShowingCondition = isShowingCondition)
            );
        combineLatest([
            this.store.select(selectIsShowingRideConditions),
            this.isSnoRideType$,
        ])
            .pipe(
                map(
                    ([isShowingRideConditions, isRideTypeSnow]) =>
                        isShowingRideConditions &&
                        isRideTypeSnow &&
                        !this.isHazardReportingEnabled
                ),
                this.setVisibleLayer(MapToolsLayerGroup.RideConditions),
                takeWhile(() => !this.isDestroyed)
            )
            .subscribe(
                (isShowingRideCondition) =>
                    (this.isShowingRideCondition = isShowingRideCondition)
            );

        this.isSnoRideType$
            .pipe(takeWhile(() => !this.isDestroyed))
            .subscribe((isSnow) => (this.isMapTypeSno = isSnow));

        this.store
            .select(selectIsShowingTrails)
            .pipe(
                map((showing) => showing && this.configService.isOffRoad),
                this.setVisibleLayer(MapToolsLayerGroup.Trails),
                takeWhile(() => !this.isDestroyed)
            )
            .subscribe(
                (isShowingTrails) => (this.isShowingTrails = isShowingTrails)
            );

        combineLatest([
            this.store.select(selectIsShowingTrailDifficulty),
            this.store.select(selectIsShowingTrails),
            this.rideType$,
        ])
            .pipe(
                map(
                    ([isShowingDifficulty, isShowingTrails, rideType]) =>
                        isShowingDifficulty &&
                        isShowingTrails &&
                        rideType === 'ORV'
                ),
                this.setVisibleLayer(MapToolsLayerGroup.TrailDifficulty),
                takeWhile(() => !this.isDestroyed)
            )
            .subscribe((showing) => (this.isShowingTrailDifficulty = showing));
        this.store
            .select(selectIsShowingHeatmap)
            .pipe(
                map((showing) => showing && this.configService.isOffRoad),
                this.setVisibleLayer(MapToolsLayerGroup.Heatmap),
                takeWhile(() => !this.isDestroyed)
            )
            .subscribe((show) => (this.isShowingHeatmap = show));
    }

    private setVisibleLayer = (layerKey: MapToolsLayerGroup) =>
        tap((active: boolean) => {
            const layerIndex = this.activeLayers.findIndex(
                (value) => value.key === layerKey
            );
            const newActiveLayers: {
                key: MapToolsLayerGroup;
                value: boolean;
            }[] = Object.assign([], this.activeLayers);
            if (layerIndex !== -1) {
                const selectedLayer = newActiveLayers[layerIndex];
                selectedLayer.value = active;
                this.activeLayers = newActiveLayers;
            } else {
                if (active) {
                    newActiveLayers.push({ key: layerKey, value: active });
                    this.activeLayers = newActiveLayers;
                }
            }
        });
}
